<template>
  <div class="mt-5">
    <div
      v-if="orderedPosts.length === 0"
      class="d-flex flex-column align-center justify-center"
      style="height: 300px; opacity: 0.6"
    >
      <p>No active posts with order numbers found in this category.</p>
      <p style="font-size: 12px">
        Run "Rank all posts" from admin settings first.
      </p>
    </div>

    <draggable
      v-else
      v-model="orderedPosts"
      item-key="uid"
      handle=".drag-handle"
      @end="onDragEnd"
      :animation="200"
    >
      <template #item="{ element, index }">
        <v-card
          flat
          class="order-card mb-2"
          :data-uid="element.uid"
          @click="loadPost(element.uid)"
        >
          <div class="d-flex align-center" style="gap: 12px">
            <div class="drag-handle" @click.stop>
              <v-icon size="20" color="grey">mdi-drag</v-icon>
            </div>

            <span
              class="order-number"
              style="
                font-size: 12px;
                font-weight: 600;
                min-width: 24px;
                text-align: center;
                color: grey;
              "
            >
              {{ index + 1 }}
            </span>

            <v-img
              v-if="element.media && element.media.length > 0"
              :src="getThumb(element.media[0])"
              width="48"
              height="48"
              cover
              style="border-radius: 6px; flex: 0 0 48px"
            />
            <div
              v-else
              style="
                width: 48px;
                height: 48px;
                border-radius: 6px;
                background: hsl(214.3 31.8% 91.4%);
                flex: 0 0 48px;
                display: flex;
                align-items: center;
                justify-content: center;
              "
            >
              <v-icon size="20" color="grey">mdi-text</v-icon>
            </div>

            <div style="flex: 1; min-width: 0">
              <p
                style="
                  font-size: 13px;
                  font-weight: 500;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  margin: 0;
                "
              >
                {{ element.title || getPreviewText(element) }}
              </p>
              <div class="d-flex align-center mt-1" style="gap: 6px">
                <v-chip
                  size="x-small"
                  :color="element.status === 'active' ? 'success' : 'grey'"
                  variant="flat"
                >
                  {{ element.status }}
                </v-chip>
                <v-chip v-if="element.recurring" size="x-small" variant="tonal">
                  <v-icon size="10" class="mr-1">mdi-repeat</v-icon>
                  Recurring
                </v-chip>
              </div>
            </div>

            <span
              v-if="element.next_publish"
              style="
                font-size: 11px;
                color: #9ca3af;
                white-space: nowrap;
                margin-left: auto;
              "
            >
              {{ formatDate(element.next_publish) }}
            </span>
          </div>
        </v-card>
      </template>
    </draggable>

    <v-snackbar v-model="saving" :timeout="-1" color="smokeDark">
      Updating order...
    </v-snackbar>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { mapState } from "vuex";

export default {
  components: { draggable },
  props: {
    posts: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      orderedPosts: [],
      saving: false,
      shuffling: false,
    };
  },
  computed: {
    ...mapState({
      workspace: (state) => state.users.userProfile.workspace,
    }),
  },
  watch: {
    posts: {
      handler(val) {
        if (this.shuffling) return;
        this.orderedPosts = [...val]
          .filter((p) => p.status === "active" && p.order_number != null)
          .sort((a, b) => (a.order_number || 0) - (b.order_number || 0));
      },
      immediate: true,
    },
  },
  methods: {
    formatDate(unix) {
      if (!unix) return "";
      const d = new Date(unix * 1000);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    },
    loadPost(uid) {
      this.$store.dispatch("loadPost", uid);
    },
    getThumb(media) {
      if (media.type === "video" && media.thumbnail) return media.thumbnail;
      return media.src || media.url || "";
    },
    getPreviewText(post) {
      if (post.variations && post.variations.length > 0) {
        return post.variations[0].text?.substring(0, 80) || "No content";
      }
      return "No content";
    },
    async shuffleAnimated() {
      if (this.orderedPosts.length < 2 || this.shuffling) return;
      this.shuffling = true;

      const cards = Array.from(
        this.$el.querySelectorAll(".order-card[data-uid]"),
      );
      const rects = cards.map((el) => el.getBoundingClientRect());

      // Build shuffle mapping: indices[newIdx] = oldIdx
      const indices = Array.from(
        { length: this.orderedPosts.length },
        (_, i) => i,
      );
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }

      // Reverse map: for old index → new index
      const newIndexOf = new Array(indices.length);
      for (let n = 0; n < indices.length; n++) {
        newIndexOf[indices[n]] = n;
      }

      // Animate each card from current position to its target position
      cards.forEach((el, oldIdx) => {
        const targetIdx = newIndexOf[oldIdx];
        const dy = rects[targetIdx].top - rects[oldIdx].top;
        if (dy === 0) return;
        el.style.transition = "none";
        el.style.transform = "translateY(0)";
        requestAnimationFrame(() => {
          el.style.transition =
            "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)";
          el.style.transform = `translateY(${dy}px)`;
        });
      });

      await new Promise((r) => setTimeout(r, 480));

      // Hide container, clear transforms, reorder array, show container
      this.$el.style.visibility = "hidden";
      cards.forEach((el) => {
        el.style.transition = "none";
        el.style.transform = "";
      });

      const newOrder = indices.map((oldIdx) => this.orderedPosts[oldIdx]);
      this.orderedPosts.splice(0, this.orderedPosts.length, ...newOrder);

      await this.$nextTick();
      this.$el.style.visibility = "";

      // Persist to Firestore
      this.saving = true;
      try {
        await Promise.all(
          this.orderedPosts.map((post, index) => {
            const newOrderNum = index + 1;
            post.order_number = newOrderNum;
            this.$store.commit("updatePostOrderNumber", {
              uid: post.uid,
              order_number: newOrderNum,
            });
            return updateDoc(
              doc(db, "workspaces", this.workspace, "posts", post.uid),
              { order_number: newOrderNum },
            );
          }),
        );
      } catch (error) {
        console.error("Error shuffling order:", error);
      }
      this.saving = false;
      this.shuffling = false;
    },
    async onDragEnd() {
      this.saving = true;
      try {
        await Promise.all(
          this.orderedPosts.map((post, index) => {
            const newOrder = index + 1;
            post.order_number = newOrder;
            return updateDoc(
              doc(db, "workspaces", this.workspace, "posts", post.uid),
              { order_number: newOrder },
            );
          }),
        );
        this.orderedPosts.forEach((post) => {
          this.$store.commit("updatePostOrderNumber", {
            uid: post.uid,
            order_number: post.order_number,
          });
        });
      } catch (error) {
        console.error("Error updating order:", error);
      }
      this.saving = false;
    },
  },
};
</script>

<style scoped>
.order-card {
  padding: 10px 14px;
  border: 1px solid hsl(214.3 31.8% 91.4%);
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  cursor: pointer;
  transition: box-shadow 0.15s;
}

.order-card:hover {
  box-shadow: 0 2px 6px 0 rgb(0 0 0 / 0.1);
}

.drag-handle {
  cursor: grab;
  padding: 4px;
  display: flex;
  align-items: center;
}

.drag-handle:active {
  cursor: grabbing;
}
</style>
